"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type ChatBotContextType = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  // true once the panel has been opened at least once — lets the (code-split)
  // panel stay unmounted until the user first reaches for it.
  hasOpened: boolean;
  pendingPrompt: string | null;
  openWithPrompt: (prompt: string) => void;
  clearPendingPrompt: () => void;
};

const ChatBotContext = createContext<ChatBotContextType | undefined>(undefined);

export const ChatBotProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpenState] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);

  const setIsOpen = useCallback((next: boolean) => {
    if (next) setHasOpened(true);
    setIsOpenState(next);
  }, []);

  const openWithPrompt = useCallback((prompt: string) => {
    setHasOpened(true);
    setPendingPrompt(prompt);
    setIsOpenState(true);
  }, []);

  const clearPendingPrompt = useCallback(() => setPendingPrompt(null), []);

  const value = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      hasOpened,
      pendingPrompt,
      openWithPrompt,
      clearPendingPrompt,
    }),
    [isOpen, setIsOpen, hasOpened, pendingPrompt, openWithPrompt, clearPendingPrompt],
  );

  return (
    <ChatBotContext.Provider value={value}>{children}</ChatBotContext.Provider>
  );
};

export const useChatBot = () => {
  const context = useContext(ChatBotContext);
  if (!context) {
    throw new Error("useChatBot must be used within a ChatBotProvider");
  }
  return context;
};
